import { getLocale } from '@arnold/core';
import { format } from 'date-fns';
import i18n from 'i18next';

export const isNullish = (value: unknown): value is null | undefined => value === undefined || value === null;

export const isEmpty = (value: unknown): value is undefined | null | '' =>
  typeof value === 'undefined' || value === null || value === '';

export const formatDate = (date: string) =>
  date
    ? format(new Date(date), 'dd.MM.yyyy', {
        locale: getLocale(i18n.language),
      })
    : '';

export function notEmpty<T>(item: T | undefined | null | '' | false): item is T {
  return !isNullish(item) && !isEmpty(item) && item !== false;
}

export const cleanArray = <T>(array?: null | ReadonlyArray<T | undefined | null | '' | false>): Array<T> =>
  (array || []).filter(notEmpty);

export const isString = (value: unknown): value is string => typeof value === 'string';
export const isNumber = (value: unknown): value is number => typeof value === 'number';
// eslint-disable-next-line @typescript-eslint/ban-types
export const isFunction = (value: unknown): value is Function => typeof value === 'function';
export const checkValueOrThrowError = <T>(value: unknown, typeCheck: (value) => value is T) => {
  if (typeCheck(value)) {
    return value;
  }
  throw new Error();
};

/**
 * Returns a promise that will be resolved when stopWaiting function returns true
 * or rejected if the wait time reaches the maximum limit
 * @param stopWaiting function to check if it should resolve
 * @param maxWaitTime maximum time to wait in ms
 * @param checkInterval how often should it ask the stopWaiting function if we can stop waiting in ms
 * @param uniqueName should be used when waitFor is called multiple times where only the last call is valid
 */
export function waitFor(
  stopWaiting: () => boolean,
  maxWaitTime = 10000,
  checkInterval = 100,
  uniqueName?: string,
  silentFail = false,
): Promise<void> {
  if (uniqueName) {
    const interval = waitForIntervals[uniqueName];
    interval && clearInterval(interval.toString() as any);
  }
  return new Promise((resolve, reject) => {
    let timePassed = 0;
    const interval = setInterval(() => {
      timePassed += checkInterval;
      if (stopWaiting()) {
        clearInterval(interval);
        resolve();
      } else if (timePassed >= maxWaitTime) {
        clearInterval(interval);
        if (!silentFail) {
          reject(new Error(`Waiting for '${stopWaiting}' reached maximum waiting time: ${maxWaitTime}ms`));
        } else {
          resolve();
        }
      }
    }, checkInterval);
    if (uniqueName) waitForIntervals[uniqueName] = interval;
  });
}

const waitForIntervals: { [key: string]: NodeJS.Timer | undefined } = {};
